package com.example.crashbit

import androidx.compose.runtime.*
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.IntSize
import com.example.crashbit.data.*
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.collectLatest
import kotlin.math.sqrt
import kotlin.random.Random

class SpaceViewModel(
    private val preferenceManager: PreferenceManager? = null,
    private val repository: UniverseRepository? = null,
    private val hapticFeedback: HapticFeedback? = null
) : ViewModel() {
    var currentScreen by mutableStateOf<Screen>(Screen.Title)
    
    // V1.5 Performance: Double-Buffering UI Snapshot logic
    var bodies: List<SpaceBody> by mutableStateOf(emptyList())
    var stars: List<SpaceBody> by mutableStateOf(emptyList())
    
    var nebulae by mutableStateOf(listOf<Nebula>())
    var wormholes by mutableStateOf(listOf<Wormhole>())
    var pendingWormholeId by mutableStateOf<Long?>(null)
    var editingWormholeId by mutableStateOf<Long?>(null)
    var scale by mutableStateOf(1f)
    var pan by mutableStateOf(Offset.Zero)
    var screenSize by mutableStateOf(IntSize.Zero)
    var screenCenter by mutableStateOf(Offset.Zero)
    var isCustomPlacing by mutableStateOf(false)
    var isCrazyEddie by mutableStateOf(false)
    
    var starsCanMerge by mutableStateOf(true)
    var starsAreDynamic by mutableStateOf(true)
    var planetsCanBeConsumed by mutableStateOf(false)
    var auLimit by mutableStateOf(10000f)
    var isPaused by mutableStateOf(false)
    var isPhotoMode by mutableStateOf(false)
    var isHabitableZoneVisible by mutableStateOf(false)
    var isHudMinimized by mutableStateOf(true)
    var selectedPlacementType by mutableStateOf(PlacementType.STAR)
    
    var timeScale by mutableFloatStateOf(1.0f)
    var isRewindConfirmationRequired by mutableStateOf(true)
    var showRewindDialog by mutableStateOf(false)
    
    var isRewindEnabled by mutableStateOf(true)
    var isDeterministicMode by mutableStateOf(false)
    var isVibrationEnabled by mutableStateOf(true)
    var hapticIntensity by mutableFloatStateOf(0.5f)
    var isCloudBackupEnabled by mutableStateOf(true)

    var mergerPassLimit by mutableIntStateOf(15)
    var gravityConstant by mutableFloatStateOf(2500f)
    var evolutionSpeed by mutableFloatStateOf(1.0f)
    var isPerformanceMeterVisible by mutableStateOf(false)
    var tps by mutableFloatStateOf(0f)
    var fps by mutableIntStateOf(0)

    var selectedBodyId by mutableStateOf<Long?>(null)
    var isFollowing by mutableStateOf(false)
    var isSurfaceViewActive by mutableStateOf(false)
    var surfacePitchAngle by mutableFloatStateOf(45f)
    var surfaceYawAngle by mutableFloatStateOf(0f)

    var inputMode by mutableStateOf(InputMode.OFF)
    var selectedPlanetSize by mutableStateOf(PlanetSize.SMALL)

    var activeEffects by mutableStateOf(listOf<VisualEffect>())
    var activeExtinctionMessage by mutableStateOf<String?>(null)
    var rainTouchPoint by mutableStateOf<Offset?>(null)
    var gameTimeNanos by mutableStateOf(0L)
    var showStats by mutableStateOf(false)
    var stats by mutableStateOf(TelemetryStats())
    var displayStrings by mutableStateOf(TelemetryDisplayStrings())
    var scannerVersion by mutableIntStateOf(0)
    var drawVersion by mutableIntStateOf(0)
    var lastActiveMode by mutableStateOf<GameMode?>(null)
    var trajectories by mutableStateOf<OracleResult?>(null)
    var interpolationAlpha by mutableFloatStateOf(1.0f) // V1.5.7

    var languageCode by mutableStateOf("en")
    var hasSeenOnboarding by mutableStateOf(false)
    var tutorialStep by mutableIntStateOf(1)
    var isTutorialActive by mutableStateOf(false)

    var savedUniverses by mutableStateOf(listOf<UniverseWithContentsModel>())

    private val simulation = UniverseSimulation(
        hapticFeedback = hapticFeedback,
        scope = viewModelScope,
        onStateUpdated = { frame ->
            viewModelScope.launch(Dispatchers.Main) {
                syncSimulationFrame(frame)
            }
        },
        onMergerEvent = { event ->
            viewModelScope.launch(Dispatchers.Main) {
                handleMergerEvent(event)
            }
        }
    )

    private fun syncSimulationFrame(frame: SimulationFrame) {
        stats = frame.stats
        displayStrings = frame.displayStrings
        scannerVersion = frame.scannerVersion
        gameTimeNanos = frame.time
        activeEffects = frame.effects
        drawVersion = frame.drawVersion
        trajectories = frame.trajectories
        tps = simulation.tps
        interpolationAlpha = frame.interpolationAlpha
        
        updateSimulationConfig()
        
        // V1.5: Atomic State Swap
        bodies = frame.bodies
        if (!isCustomPlacing) {
            stars = frame.stars
        }

        // Section 5: Double-Buffering UI Snapshot
        // Freeze positions on the Main Thread for jitter-free follow
        bodies.forEach { 
            it.renderPosition = it.position 
            it.renderVelocity = it.velocity
        }
        stars.forEach { 
            it.renderPosition = it.position 
            it.renderVelocity = it.velocity
        }
    }


    private fun updateSimulationConfig() {
        val activeMode = (currentScreen as? Screen.Sandbox)?.mode ?: lastActiveMode
        val modeAllowsDynamicStars = (activeMode == GameMode.CRAZY_EDDIE || activeMode == GameMode.CUSTOM || activeMode == GameMode.LOADED)
        val effectiveStarsAreDynamic = modeAllowsDynamicStars && starsAreDynamic
        val effectiveStarsCanMerge = modeAllowsDynamicStars && starsCanMerge

        simulation.isCrazyEddie = isCrazyEddie
        simulation.starsCanMerge = effectiveStarsCanMerge
        simulation.starsAreDynamic = effectiveStarsAreDynamic
        simulation.planetsCanBeConsumed = planetsCanBeConsumed
        simulation.auLimit = auLimit
        simulation.isPaused = isPaused
        simulation.timeScale = timeScale
        simulation.isDeterministicMode = isDeterministicMode
        simulation.isVibrationEnabled = isVibrationEnabled
        simulation.hapticIntensity = hapticIntensity
        simulation.pan = pan
        simulation.scale = scale
        simulation.screenSize = screenSize
        simulation.screenCenter = screenCenter
        simulation.selectedBodyId = selectedBodyId
        simulation.isCustomPlacing = isCustomPlacing
        simulation.currentScreen = currentScreen
        simulation.lastActiveMode = lastActiveMode
        simulation.rainTouchPoint = rainTouchPoint
        simulation.inputMode = inputMode
        simulation.selectedPlanetSize = selectedPlanetSize
        simulation.nebulae = nebulae

        simulation.config = SimulationConfig(
            g = gravityConstant,
            mergerPassLimit = mergerPassLimit,
            evolutionSpeed = evolutionSpeed,
            auLimit = auLimit,
            planetsCanBeConsumed = planetsCanBeConsumed,
            starsCanMerge = effectiveStarsCanMerge,
            starsAreDynamic = effectiveStarsAreDynamic,
            wormholes = wormholes
        )
    }

    private fun handleMergerEvent(event: MergerEvent) {
        if (selectedBodyId != null && event.consumedIds.contains(selectedBodyId!!)) {
            selectedBodyId = event.producedIds.firstOrNull()
            if (selectedBodyId == null) isSurfaceViewActive = false
        }
        if (event.lostJordyPopulation > 0L) {
            val lostCountStr = formatJordyCount(event.lostJordyPopulation)
            activeExtinctionMessage = lostCountStr
            viewModelScope.launch {
                delay(5000)
                if (activeExtinctionMessage == lostCountStr) activeExtinctionMessage = null
            }
        }
    }

    init {
        if (repository != null) {
            viewModelScope.launch { repository.allUniverses.collectLatest { savedUniverses = it } }
        }
        if (preferenceManager != null) {
            viewModelScope.launch { preferenceManager.hasSeenOnboarding.collect { hasSeenOnboarding = it; if (!it && currentScreen is Screen.Title) currentScreen = Screen.Onboarding } }
            viewModelScope.launch { preferenceManager.languageCode.collect { languageCode = it } }
            viewModelScope.launch { preferenceManager.showPerformance.collect { isPerformanceMeterVisible = it } }
            viewModelScope.launch { preferenceManager.starsCanMerge.collect { starsCanMerge = it } }
            viewModelScope.launch { preferenceManager.starsAreDynamic.collect { starsAreDynamic = it } }
            viewModelScope.launch { preferenceManager.planetsCanBeConsumed.collect { planetsCanBeConsumed = it } }
            viewModelScope.launch { preferenceManager.auLimit.collect { auLimit = it } }
            viewModelScope.launch { preferenceManager.mergerPassLimit.collect { mergerPassLimit = it } }
            viewModelScope.launch { preferenceManager.evolutionSpeed.collect { evolutionSpeed = it } }
            viewModelScope.launch { preferenceManager.rewindEnabled.collect { isRewindEnabled = it } }
            viewModelScope.launch { preferenceManager.deterministicMode.collect { isDeterministicMode = it } }
            viewModelScope.launch { preferenceManager.vibrationEnabled.collect { isVibrationEnabled = it } }
            viewModelScope.launch { preferenceManager.hapticIntensity.collect { hapticIntensity = it } }
            viewModelScope.launch { preferenceManager.isCloudBackupEnabled.collect { isCloudBackupEnabled = it } }
        } else {
            hasSeenOnboarding = true
        }
    }

    fun selectLanguage(code: String) {
        viewModelScope.launch {
            preferenceManager?.setLanguageCode(code); preferenceManager?.setHasSeenOnboarding(true)
            currentScreen = Screen.Title
        }
    }

    fun updateShowPerformance(v: Boolean) { isPerformanceMeterVisible = v; viewModelScope.launch { preferenceManager?.setShowPerformance(v) } }
    fun updateStarsCanMerge(v: Boolean) { starsCanMerge = v; viewModelScope.launch { preferenceManager?.setStarsCanMerge(v) } }
    fun updateStarsAreDynamic(v: Boolean) { starsAreDynamic = v; viewModelScope.launch { preferenceManager?.setStarsAreDynamic(v) } }
    fun updatePlanetsConsumed(v: Boolean) { planetsCanBeConsumed = v; viewModelScope.launch { preferenceManager?.setPlanetsCanBeConsumed(v) } }
    fun updateAuLimit(v: Float) { auLimit = v; viewModelScope.launch { preferenceManager?.setAuLimit(v) } }
    fun updateMergerPassLimit(v: Int) { mergerPassLimit = v; viewModelScope.launch { preferenceManager?.setMergerPassLimit(v) } }
    fun updateEvolutionSpeed(v: Float) { evolutionSpeed = v; viewModelScope.launch { preferenceManager?.setEvolutionSpeed(v) } }
    fun updateRewindEnabled(v: Boolean) { isRewindEnabled = v; viewModelScope.launch { preferenceManager?.setRewindEnabled(v) } }
    fun updateDeterministicMode(v: Boolean) { isDeterministicMode = v; viewModelScope.launch { preferenceManager?.setDeterministicMode(v) } }
    fun updateVibrationEnabled(v: Boolean) { isVibrationEnabled = v; viewModelScope.launch { preferenceManager?.setVibrationEnabled(v) } }
    fun updateHapticIntensity(v: Float) { hapticIntensity = v; viewModelScope.launch { preferenceManager?.setHapticIntensity(v) } }
    fun updateCloudBackupEnabled(v: Boolean) { isCloudBackupEnabled = v; viewModelScope.launch { preferenceManager?.setCloudBackupEnabled(v) } }

    fun calculateCenterOfMass(): Offset {
        var totalMass = 0f
        var weightedX = 0f
        var weightedY = 0f

        // System Gravity Well is anchored strictly to stellar class objects (suns and singularities)
        stars.forEach {
            totalMass += it.mass
            weightedX += it.position.x * it.mass
            weightedY += it.position.y * it.mass
        }

        return if (totalMass > 0f) Offset(weightedX / totalMass, weightedY / totalMass) else Offset.Zero
    }

    fun recenterOnGravitationalCenter() {
        isFollowing = false
        val center = calculateCenterOfMass()
        pan = -center
    }

    fun isSystemOffScreen(): Boolean {
        if (stars.isEmpty()) return false
        val center = calculateCenterOfMass()
        val cameraWorldPos = -pan
        val pannedDistance = (cameraWorldPos - center).getDistance()
        val threshold = if (scale > 0f) 2500f / scale else 3000f
        return pannedDistance > threshold
    }

    fun completeTutorial() {
        isTutorialActive = false
        hasSeenOnboarding = true
        viewModelScope.launch {
            preferenceManager?.setHasSeenOnboarding(true)
        }
    }

    fun startTutorial() {
        tutorialStep = 1
        isTutorialActive = true
    }

    fun advanceTutorialStep() {
        if (tutorialStep < 5) {
            tutorialStep += 1
        } else {
            completeTutorial()
        }
    }

    fun startNewGame(mode: GameMode) {
        selectedBodyId = null; isFollowing = false; isSurfaceViewActive = false; recenterSurfaceView()
        bodies = emptyList(); stars = emptyList(); nebulae = emptyList(); wormholes = emptyList()
        pendingWormholeId = null; editingWormholeId = null; scale = 1f; pan = Offset.Zero; activeEffects = emptyList()
        isCrazyEddie = (mode == GameMode.CRAZY_EDDIE)
        isCustomPlacing = (mode == GameMode.CRAZY_EDDIE) // Enable placement for Crazy Eddie (integrated Worm Vector)
        lastActiveMode = mode; starsAreDynamic = (mode == GameMode.CRAZY_EDDIE)
        stars = getInitialStars(mode); initializeNebulae(mode)
        
        if (mode == GameMode.CRAZY_EDDIE) {
            initializeWormholes() // Crazy Eddie now acts as Worm Vector
        }
        
        inputMode = InputMode.OFF; currentScreen = Screen.Sandbox(mode); isHabitableZoneVisible = (mode == GameMode.STABLE_SYSTEM)
        if (!hasSeenOnboarding) {
            startTutorial()
        }
        updateSimulationConfig(); simulation.reset(bodies, stars)
    }

    fun resumeActiveSession() {
        val mode = lastActiveMode ?: return
        currentScreen = Screen.Sandbox(mode); updateSimulationConfig(); simulation.load(bodies, stars)
    }

    fun updateScreenSize(size: IntSize) { screenCenter = Offset(size.width / 2f, size.height / 2f); screenSize = size }

    private fun getInitialStars(mode: GameMode): List<SpaceBody> = when (mode) {
        GameMode.SINGLE -> listOf(simulation.createStar(Offset.Zero))
        GameMode.BINARY -> listOf(simulation.createStar(Offset(-250f, 0f)), simulation.createStar(Offset(250f, 0f)))
        GameMode.STABLE_SYSTEM -> listOf(simulation.createStar(Offset.Zero))
        else -> emptyList()
    }

    private fun initializeWormholes() { 
        isCustomPlacing = true
        selectedPlacementType = if (isSaveSupported) PlacementType.WORMHOLE else PlacementType.STAR
    }

    fun addCustomWormhole(worldPos: Offset) {
        if (!isSaveSupported) return
        if (wormholes.size < 10) {
            val pendingId = pendingWormholeId
            if (pendingId == null) {
                val colors = listOf(Color.Cyan, Color.Magenta, Color(0xFFFFD700), Color.Green, Color(0xFFFF4500))
                val color = colors[(wormholes.size / 2) % 5]
                val newHole = Wormhole(position = worldPos, color = color)
                pendingWormholeId = newHole.id
                wormholes = wormholes + newHole
            } else {
                val firstHole = wormholes.find { it.id == pendingId }
                if (firstHole != null) {
                    val secondId = Random.nextLong()
                    wormholes = wormholes.map { 
                        if (it.id == pendingId) it.copy(linkedId = secondId) else it 
                    } + Wormhole(id = secondId, position = worldPos, color = firstHole.color, linkedId = pendingId)
                }
                pendingWormholeId = null
            }
            updateSimulationConfig()
            if (isPaused) updateTrajectories()
        }
    }

    fun rotateWormhole(id: Long) { wormholes = wormholes.map { if (it.id == id) it.copy(exitAngle = (it.exitAngle + 45f) % 360f) else it }; updateSimulationConfig(); if (isPaused) updateTrajectories() }
    fun setWormholeAngle(id: Long, angle: Float) { wormholes = wormholes.map { if (it.id == id) it.copy(exitAngle = angle) else it }; updateSimulationConfig(); if (isPaused) updateTrajectories() }

    private fun initializeNebulae(mode: GameMode) {
        nebulae = when (mode) {
            GameMode.CRAZY_EDDIE, GameMode.BINARY -> List(3) { simulation.createRandomNebula() }
            GameMode.STABLE_SYSTEM -> listOf(simulation.createRandomNebula(Offset(1500f, 1500f)))
            else -> emptyList()
        }
    }

    fun yeetPlanet(position: Offset, velocity: Offset, mass: Float, radius: Float, color: Color) {
        val planet = SpaceBody(id = Random.nextLong(), name = simulation.generateCosmicName(false), position = position, velocity = velocity, mass = mass, radius = radius, color = color, composition = simulation.generateInitialComposition(false, mass), rotationSpeed = (Random.nextFloat() * 60 - 30), hasRings = Random.nextFloat() < 0.15f)
        addBody(planet)
    }

    fun addCustomStar(worldPos: Offset) {
        if (stars.size < 10) {
            val totalM = stars.sumOf { it.mass.toDouble() }.toFloat()
            val initialV = if (stars.isNotEmpty()) {
                val center = Offset(stars.sumOf { (it.position.x * it.mass).toDouble() }.toFloat() / totalM, stars.sumOf { (it.position.y * it.mass).toDouble() }.toFloat() / totalM)
                val diff = worldPos - center; val dist = diff.getDistance()
                if (dist > 10f) {
                    val speed = sqrt((PhysicsEngine.G * totalM) / dist) * 1.5f; val tangent = Offset(-diff.y, diff.x).let { val d = it.getDistance(); if (d > 0) it / d else Offset.Zero }
                    tangent * speed * (0.85f + Random.nextFloat() * 0.3f)
                } else Offset.Zero
            } else Offset.Zero
            if (stars.isNotEmpty() && initialV != Offset.Zero) { val newSM = 100000f; val recoilV = -(initialV * newSM) / totalM; stars.forEach { it.velocity += recoilV } }
            val newStar = simulation.createStar(worldPos, initialV); stars = stars + newStar; simulation.stars.add(newStar)
            if (isPaused) updateTrajectories()
        }
    }

    fun addCustomNebula(worldPos: Offset) { val newN = simulation.createRandomNebula(worldPos); nebulae = nebulae + newN; simulation.nebulae = simulation.nebulae + newN; if (isPaused) updateTrajectories() }
    fun finishPlacement() { if (pendingWormholeId != null) { wormholes = wormholes.filter { it.id != pendingWormholeId }; pendingWormholeId = null }; isCustomPlacing = false; updateSimulationConfig(); simulation.load(bodies, stars); if (isPaused) updateTrajectories() }
    fun addBody(body: SpaceBody) { val jitter = Offset((Random.nextFloat() * 0.02f - 0.01f), (Random.nextFloat() * 0.02f - 0.01f)); simulation.spawnQueue.trySend(body.copy(position = body.position + jitter)); if (isPaused) updateTrajectories() }
    fun setRainPoint(point: Offset?) { rainTouchPoint = point }
    fun cycleInputMode() { inputMode = when (inputMode) { InputMode.OFF -> InputMode.YEET; InputMode.YEET -> InputMode.RAIN; InputMode.RAIN -> InputMode.OFF } }
    fun toggleStats() { showStats = !showStats }
    fun togglePause() { isPaused = !isPaused; updateSimulationConfig(); updateTrajectories() }
    fun togglePhotoMode() { isPhotoMode = !isPhotoMode }
    fun toggleHabitableZone() { isHabitableZoneVisible = !isHabitableZoneVisible }
    fun cycleTimeScale() { timeScale = when (timeScale) { 1.0f -> 2.0f; 2.0f -> 0.1f; 0.1f -> 0.5f; else -> 1.0f }; updateSimulationConfig() }
    fun triggerRewind() { if (isRewindConfirmationRequired) { showRewindDialog = true; isPaused = true; updateSimulationConfig() } else confirmRewind() }
    fun confirmRewind() { simulation.performRewind(); showRewindDialog = false; isPaused = false; updateSimulationConfig() }
    fun cancelRewind() { showRewindDialog = false; isPaused = false; updateSimulationConfig() }
    fun selectBody(id: Long?) { selectedBodyId = id; if (id == null) { isFollowing = false; isSurfaceViewActive = false; recenterSurfaceView() } else isHudMinimized = true }
    fun toggleFollow() { if (selectedBodyId != null) { isFollowing = !isFollowing; if (!isFollowing) { isSurfaceViewActive = false; recenterSurfaceView() } } }
    fun deselectBody() { selectedBodyId = null; isFollowing = false; isSurfaceViewActive = false; recenterSurfaceView() }
    fun enterSurfaceView() { if (selectedBodyId != null) { isFollowing = true; isSurfaceViewActive = true; recenterSurfaceView() } }
    fun exitSurfaceView() { isSurfaceViewActive = false; recenterSurfaceView() }
    fun updateSurfacePitchAndYaw(deltaX: Float, deltaY: Float) {
        surfaceYawAngle = (surfaceYawAngle + deltaX * 0.3f) % 360f
        surfacePitchAngle = (surfacePitchAngle - deltaY * 0.3f).coerceIn(-90f, 90f)
    }
    fun recenterSurfaceView() {
        surfacePitchAngle = 45f
        surfaceYawAngle = 0f
    }
    fun updateTrajectories() { updateSimulationConfig(); simulation.updateTrajectories() }
    fun zoomIn() { scale = (scale * 1.2f).coerceIn(0.005f, 10f) }
    fun zoomOut() { scale = (scale / 1.2f).coerceIn(0.005f, 10f) }
    fun saveGame(name: String) { viewModelScope.launch { repository?.saveUniverse(name, WorldState(bodies, stars, nebulae, wormholes, scale, pan, isCrazyEddie, starsCanMerge, starsAreDynamic, planetsCanBeConsumed, auLimit, isPaused)) }; val mode = (currentScreen as? Screen.Sandbox)?.mode ?: GameMode.CUSTOM; currentScreen = Screen.Sandbox(mode, name) }
    fun deleteGame(universe: UniverseModel) { viewModelScope.launch { repository?.deleteUniverse(universe) } }
    fun loadUniverse(uWithC: UniverseWithContentsModel) {
        selectedBodyId = null; isFollowing = false; isSurfaceViewActive = false; recenterSurfaceView()
        val u = uWithC.universe; bodies = uWithC.bodies.filter { !it.isStar }.map { it.toModel() }; stars = uWithC.bodies.filter { it.isStar }.map { it.toModel() }; nebulae = uWithC.nebulae.map { it.toModel() }
        scale = u.scale; pan = Offset(u.panX, u.panY); isCrazyEddie = u.isCrazyEddie; starsCanMerge = u.starsCanMerge; starsAreDynamic = u.starsAreDynamic; planetsCanBeConsumed = u.planetsCanBeConsumed; auLimit = u.auLimit; isPaused = u.isPaused
        isCustomPlacing = false; lastActiveMode = GameMode.LOADED; currentScreen = Screen.Sandbox(GameMode.LOADED, u.name); updateSimulationConfig(); simulation.load(bodies, stars); if (isPaused) updateTrajectories()
    }

    fun backToTitle() { selectedBodyId = null; isFollowing = false; isSurfaceViewActive = false; recenterSurfaceView(); pendingWormholeId = null; editingWormholeId = null; currentScreen = Screen.Title; updateSimulationConfig() }
    fun openAbout() { currentScreen = Screen.About; updateSimulationConfig() }
    fun navigateToBrowser() { currentScreen = Screen.Browser; updateSimulationConfig() }
    fun resetToDefaults() { 
        viewModelScope.launch {
            preferenceManager?.setShowPerformance(false)
            preferenceManager?.setStarsCanMerge(true)
            preferenceManager?.setStarsAreDynamic(true)
            preferenceManager?.setPlanetsCanBeConsumed(false)
            preferenceManager?.setAuLimit(10000f)
            preferenceManager?.setMergerPassLimit(15)
            preferenceManager?.setEvolutionSpeed(1.0f)
            preferenceManager?.setRewindEnabled(true)
            preferenceManager?.setDeterministicMode(false)
            preferenceManager?.setVibrationEnabled(true)
            preferenceManager?.setHapticIntensity(0.5f)
            preferenceManager?.setCloudBackupEnabled(true)
        }
    }

    fun triggerTestHaptic(targetIntensity: Float = hapticIntensity) { hapticFeedback?.triggerHaptic(1000f, targetIntensity) }
    private fun Offset.getDistance() = sqrt((x * x + y * y).toDouble()).toFloat()
}

fun formatJordyCount(count: Long): String {
    return when {
        count >= 1_000_000_000_000L -> "${(count / 100_000_000_000L) / 10.0} Trillion"
        count >= 1_000_000_000L -> "${(count / 100_000_000L) / 10.0} Billion"
        count >= 1_000_000L -> "${(count / 100_000L) / 10.0} Million"
        count >= 1_000L -> "${(count / 100L) / 10.0} Thousand"
        else -> count.toString()
    }
}
